import { Injectable } from '@angular/core';

import { Observable, BehaviorSubject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import { BaseClass } from 'src/app/core/base-class';
import { RuleService } from 'src/app/services/rule.service';
import { IDataSource } from 'src/app/components/dynamic-table';
import { IPaginator } from 'src/app/shared/model/paginator.model';
import { IRules } from 'src/app/shared/interfaces/rule.interfaces';

@Injectable()
export class BucketFacade extends BaseClass {
  readonly dataSource$: Observable<IDataSource[]>;
  readonly paginationSource$: Observable<IPaginator>;

  private _pagination = 1;

  private _dataSource$ = new BehaviorSubject(undefined);
  private _paginationSource$ = new BehaviorSubject(undefined);

  constructor(private ruleService: RuleService) {
    super();

    this.dataSource$ = this._dataSource$.asObservable();
    this.paginationSource$ = this._paginationSource$.asObservable();
  }

  getAll(versionId: number): void {
    this._dataSource$.next(undefined);
    this.ruleService
      .getBucketList({
        versionId,
        page: this._pagination,
        size: 20,
        sort_by: 'created_at,desc',
      })
      .pipe(
        takeUntil(this.unsubscribe),
        take(1),
        map((response) => ({
          data: response.items,
          pagination: {
            currentPage: response.page,
            pageSize: response.size,
            totalItems: response.total,
          },
        }))
      )
      .subscribe({
        next: ({ data, pagination }) => {
          const preparedData = data.map((rule) => ({
            ...rule,
            category_name: rule.template.template_category.label,
          }));
          this._dataSource$.next(preparedData);
          this._paginationSource$.next(pagination);
        },
        error: () => this._dataSource$.next(null),
      });
  }

  getHierarchyDependents(versionId: number, ruleId?: number) {
    return this.ruleService
      .getRulesList({
        versionId,
        all: true,
        ...(ruleId && { rule_id: ruleId }),
      })
      .pipe(
        takeUntil(this.unsubscribe),
        take(1),
        map(({ items }: IRules) =>
          items
            .map(
              ({ labor_union, location_group, location }) =>
                labor_union?.name || location_group?.name || location?.name
            )
            .filter((name) => name !== undefined && name !== null)
            .sort((a, b) => a.localeCompare(b))
        )
      );
  }

  setPagination(versionId: number, pagination: number): void {
    this._pagination = pagination;
    this.getAll(versionId);
  }
}
